import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import type { FC } from 'react';
import { RADIO_GROUP_INPUT_TEST_IDS } from './constants';
import InputError from '~/components/feedback/InputError';
import type { IDropdownOption, IImageRadioGroupInput } from '~/types/inputs';

const RadioGroup: FC<IImageRadioGroupInput> = ({ name, label, required, options }) => {
	const [field, meta] = useField(name);
	const { submitCount } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	return (
		<fieldset data-testid={RADIO_GROUP_INPUT_TEST_IDS.ROOT} className="col-span-6">
			<legend
				className="font-medium text-sm mb-3"
				data-testid={RADIO_GROUP_INPUT_TEST_IDS.LABEL}
			>
				{label}
				{required && !label.endsWith('*') ? <sup>*</sup> : null}
			</legend>
			<div className="grid grid-cols-2 gap-4">
				{options.map((option: IDropdownOption) => {
					const optionId = `${field.name}_${option.value}`;
					const isSelected = option.value === field.value;

					return (
						<div className="relative text-center" key={optionId}>
							<div className="relative cursor-pointer w-full aspect-square rounded-[10px] mb-1">
								<input
									name={field.name}
									onChange={field.onChange}
									value={option.value}
									type="radio"
									id={optionId}
									checked={isSelected}
									className="peer absolute inset-0 opacity-0 cursor-pointer"
									data-testid={RADIO_GROUP_INPUT_TEST_IDS.INPUT}
								/>
								<div
									className={clsx(
										'hidden peer-checked:inline-flex absolute top-2 left-2 w-checkbox h-checkbox rounded-checkbox border-2 items-center justify-center',
									)}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="18.353"
										height="14.011"
										viewBox="0 0 18.353 14.011"
									>
										<g>
											<path
												d="m2.121 7.547 4.343 4.343 9.768-9.769"
												fill="none"
												stroke="currentColor"
												strokeLinecap="round"
												strokeWidth="3"
											/>
										</g>
									</svg>
								</div>
								<img
									src={option.image_url}
									alt=""
									className="object-cover peer-focus:ring-4 peer-focus:ring-checkbox-checked/40 rounded-[10px] border-2 border-[#BF984B] peer-checked:border-[#EA1522]"
								/>
							</div>
							<label
								htmlFor={optionId}
								className="cursor-pointer ml-3 text-sm font-medium form-label text-left font-large-body font-weight-body"
							>
								{option.label}
							</label>
						</div>
					);
				})}
			</div>
			<InputError name={name} isError={isError}>
				{meta.error}
			</InputError>
		</fieldset>
	);
};

export default RadioGroup;
